import { PDFType } from '@prisma/client';

export const DOCUMENT_BLOCK_KEYS = [
	'header',
	'inclusionsExclusions',
	'jobDetails',
	'lineItems',
	'notes',
	'paymentGrid',
	'signature',
	'terms',
	'totalsBox',
] as const;

export type DocumentBlockKey = (typeof DOCUMENT_BLOCK_KEYS)[number];

declare global {
	// eslint-disable-next-line @typescript-eslint/no-namespace
	namespace PrismaJson {
		export type DocumentBlockKey = (typeof DOCUMENT_BLOCK_KEYS)[number];
	}
}

export const DEFAULT_DOCUMENT_LAYOUT_BLOCKS: DocumentBlockKey[] = [
	'header',
	'jobDetails',
	'paymentGrid',
	'lineItems',
	'notes',
	'inclusionsExclusions',
	'totalsBox',
	'terms',
	'signature',
];

const DOCUMENT_BLOCK_KEY_SET = new Set<string>(DOCUMENT_BLOCK_KEYS);

const BLOCK_AVAILABILITY: Record<PDFType, DocumentBlockKey[]> = {
	[PDFType.QUOTE]: ['header', 'jobDetails', 'lineItems', 'notes', 'inclusionsExclusions', 'totalsBox', 'terms', 'signature'],
	[PDFType.INVOICE]: ['header', 'jobDetails', 'lineItems', 'notes', 'inclusionsExclusions', 'totalsBox', 'terms', 'signature'],
	[PDFType.RECEIPT]: ['header', 'jobDetails', 'paymentGrid', 'lineItems', 'totalsBox', 'terms'],
};

export const DOCUMENT_BLOCK_LABELS: Record<DocumentBlockKey, string> = {
	header: 'Header',
	jobDetails: 'Job Details',
	paymentGrid: 'Payment History',
	lineItems: 'Line Items',
	notes: 'Notes',
	inclusionsExclusions: 'Inclusions & Exclusions',
	totalsBox: 'Totals',
	terms: 'Terms',
	signature: 'Signature',
};

export const orderAllBlocks = (blocks: string[] | null): DocumentBlockKey[] => {
	const seen = new Set<DocumentBlockKey>();
	const ordered: DocumentBlockKey[] = [];

	for (const key of blocks ?? []) {
		if (isDocumentBlockKey(key) && !seen.has(key)) {
			ordered.push(key);
			seen.add(key);
		}
	}

	for (const key of DEFAULT_DOCUMENT_LAYOUT_BLOCKS) {
		if (!seen.has(key)) {
			ordered.push(key);
			seen.add(key);
		}
	}

	return ordered;
};

export const isDocumentBlockKey = (value: string): value is DocumentBlockKey => DOCUMENT_BLOCK_KEY_SET.has(value);

export const parseBlockArray = (value: unknown): DocumentBlockKey[] | null => {
	if (!Array.isArray(value)) {
		return null;
	}

	return value.filter((entry): entry is DocumentBlockKey => typeof entry === 'string' && isDocumentBlockKey(entry));
};

export const resolveQuoteLayoutOrder = ({
	docType,
	layoutBlocks,
	override,
}: {
	docType?: PDFType | null;
	layoutBlocks: string[];
	override?: string[] | null;
}): DocumentBlockKey[] => {
	const available = BLOCK_AVAILABILITY[docType ?? PDFType.QUOTE];
	const availableSet = new Set<string>(available);

	const source = override ?? layoutBlocks;
	const seen = new Set<DocumentBlockKey>();
	const ordered: DocumentBlockKey[] = [];

	for (const key of source) {
		if (isDocumentBlockKey(key) && availableSet.has(key) && !seen.has(key)) {
			ordered.push(key);
			seen.add(key);
		}
	}

	for (const key of available) {
		if (!seen.has(key)) {
			ordered.push(key);
			seen.add(key);
		}
	}

	return ordered;
};
